import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import PageMeta from "@/components/PageMeta";
import {
  Loader2, Lock, Download, ChevronLeft, ChevronRight, Sparkles,
  Clock, Presentation, GalleryHorizontalEnd, Zap, Gift, HelpCircle,
} from "lucide-react";

/**
 * Public Share Page
 *
 * Anonymous viewer flow:
 *   1. Email gate (no Wokelo watermark on the deck itself, but the page
 *      chrome shows the Wokelo brand — sidebar with greyed-out nav items
 *      is rendered behind the gate / carousel so the page never feels empty).
 *   2. POST /unlock → cookie set → carousel + Download button.
 *   3. Soft "Try Wokelo free" CTA at the bottom.
 *
 * Per user direction: brand the *page chrome* (sidebar) but NOT the
 * deck content itself — mimics the real app layout but read-only.
 */
export default function PublicSharePage() {
  const { token } = useParams();
  const [stage, setStage] = useState("loading"); // loading | gate | viewing | error
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const carouselRef = useRef(null);

  const fetchMeta = async () => {
    try {
      const res = await axios.get(`${API}/share/${token}/meta`, {
        withCredentials: true,
      });
      setMeta(res.data);
      setStage("viewing");
    } catch (err) {
      if (err.response?.status === 401) {
        setStage("gate");
      } else if (err.response?.status === 404) {
        setStage("error");
        setErrorMsg("This share link is invalid or has been revoked.");
      } else {
        setStage("error");
        setErrorMsg("Couldn't load this presentation. Please try again.");
      }
    }
  };

  useEffect(() => { fetchMeta(); }, [token]);  // eslint-disable-line

  const handleUnlock = async (e) => {
    e?.preventDefault?.();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/share/${token}/unlock`, { email },
        { withCredentials: true },
      );
      await fetchMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't unlock — please try again.");
    } finally { setSubmitting(false); }
  };

  const handleDownload = () => {
    const url = `${API}/share/${token}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Keyboard nav.
  useEffect(() => {
    if (stage !== "viewing") return undefined;
    const onKey = (e) => {
      if (!meta?.slide_thumbnail_ids?.length) return;
      if (e.key === "ArrowRight") setActiveIdx(i => Math.min(meta.slide_thumbnail_ids.length - 1, i + 1));
      if (e.key === "ArrowLeft") setActiveIdx(i => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stage, meta]);

  const total = meta?.slide_thumbnail_ids?.length || 0;
  const currentThumbId = meta?.slide_thumbnail_ids?.[activeIdx];

  useEffect(() => {
    if (!carouselRef.current) return;
    const el = carouselRef.current.querySelector(`[data-rail-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeIdx]);

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <ShareShell>
      {/* Public share links are private-by-email. Search engines must
          never index these URLs even if a user copies them publicly. */}
      <PageMeta noindex />
      {stage === "loading" && (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" data-testid="share-loading" />
        </div>
      )}

      {stage === "error" && (
        <div className="flex-1 flex items-center justify-center px-6">
          <Card className="p-8 max-w-md text-center" data-testid="share-error">
            <h1 className="text-xl font-semibold mb-2">Link unavailable</h1>
            <p className="text-sm text-gray-500 mb-6">{errorMsg}</p>
            <Link to="/signup" className="text-teal-600 hover:underline text-sm">
              Create your own deck on Wokelo →
            </Link>
          </Card>
        </div>
      )}

      {stage === "gate" && (
        <EmailGate
          email={email} setEmail={setEmail}
          onSubmit={handleUnlock} submitting={submitting}
        />
      )}

      {stage === "viewing" && (
        <Viewer
          meta={meta}
          activeIdx={activeIdx}
          setActiveIdx={setActiveIdx}
          total={total}
          currentThumbId={currentThumbId}
          carouselRef={carouselRef}
          onDownload={handleDownload}
          token={token}
        />
      )}
    </ShareShell>
  );
}


/* ── Shell (sidebar + content) ───────────────────────────────────────── */

function ShareShell({ children }) {
  return (
    <div className="app-layout" data-testid="share-shell">
      <PublicShareSidebar />
      <main className="main-content flex flex-col min-h-screen bg-[var(--content-bg,#fafafa)]">
        {children}
      </main>
    </div>
  );
}


/**
 * Read-only mirror of the real sidebar — same logo, same nav items,
 * same color palette — but every link is rendered as a non-interactive
 * span so anonymous visitors can see the brand without being able to
 * navigate into authenticated routes. This is what the user explicitly
 * asked for: "navbar with tabs greyed out".
 */
function PublicShareSidebar() {
  return (
    <aside className="sidebar" data-testid="share-public-sidebar">
      <div className="sidebar-logo">
        <div className="flex flex-col items-start">
          <img
            src="https://customer-assets.emergentagent.com/job_0ff4c3b6-dbc6-4fa3-9f4e-495aa3aa78ed/artifacts/eubmytrj_wokelo-full%20logo-white-color%20%281%29.png"
            alt="Wokelo"
            className="h-5 mb-2"
          />
          <span className="text-sm font-bold text-white tracking-tight font-['Manrope']">Slides by Wokelo</span>
        </div>
      </div>
      <nav className="sidebar-nav">
        <SubtleNavItem icon={Presentation} label="PPT Studio" />
        <SubtleNavItem icon={Clock} label="History" />
        <SubtleNavItem icon={Sparkles} label="Personalize" />
        <SubtleNavItem icon={GalleryHorizontalEnd} label="Gallery" />
      </nav>
      <div className="sidebar-bottom">
        <SubtleNavItem icon={Zap} label="Upgrade Plan" bottom />
        <SubtleNavItem icon={Gift} label="Refer to earn credits" bottom />
        <SubtleNavItem icon={HelpCircle} label="Help" bottom />
      </div>
    </aside>
  );
}


function SubtleNavItem({ icon: Icon, label, bottom = false }) {
  // Greyed-out at rest, but clickable: routes to /signup. The hover state
  // is intentionally minimal (slight opacity lift, no color shift) so the
  // sidebar still reads as "branding only" — not a pushy CTA. Anyone
  // curious enough to click gets gently funneled to signup.
  const baseClass = bottom ? "sidebar-bottom-link" : "sidebar-link";
  return (
    <Link
      to="/signup"
      className={`${baseClass} share-disabled-nav`}
      data-testid={`share-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      title="Sign up to use Wokelo"
    >
      <Icon className="w-4 h-4" />{label}
    </Link>
  );
}


/* ── Email gate ──────────────────────────────────────────────────────── */

function EmailGate({ email, setEmail, onSubmit, submitting }) {
  return (
    <div className="flex-1 flex items-center justify-center px-6 py-12">
      <Card className="p-8 sm:p-10 max-w-md w-full shadow-md border-gray-200" data-testid="share-gate">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-teal-50 flex items-center justify-center">
            <Lock className="w-4 h-4 text-teal-600" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900">View shared presentation</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6 leading-relaxed">
          Enter your email to preview the slides and download the file.
        </p>
        <form onSubmit={onSubmit} className="space-y-3">
          <Input
            type="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="share-gate-email"
            className="h-11"
            autoFocus
          />
          <Button
            type="submit"
            disabled={!email || submitting}
            className="w-full bg-black hover:bg-gray-800 h-11"
            data-testid="share-gate-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock preview"}
          </Button>
        </form>
        <p className="text-[11px] text-gray-400 mt-4 leading-relaxed">
          We'll only use this email to unlock the file. By continuing you agree
          to receive occasional product updates.
        </p>
        <div className="mt-6 pt-5 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-500 mb-2">Want to make your own?</p>
          <Link to="/signup" className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 hover:text-teal-700">
            <Sparkles className="w-3.5 h-3.5" />
            Create a free Wokelo account
          </Link>
        </div>
      </Card>
    </div>
  );
}


/* ── Viewer (carousel + download + soft CTA) ─────────────────────────── */

function Viewer({
  meta, activeIdx, setActiveIdx, total, currentThumbId,
  carouselRef, onDownload, token,
}) {
  return (
    <div className="flex-1 flex flex-col" data-testid="share-viewer">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate" data-testid="share-title">
              {meta?.title || "Shared presentation"}
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {meta?.kind === "deck"
                ? `${meta?.num_slides || total} slides`
                : "1 slide"}
            </p>
          </div>
          <Button
            onClick={onDownload}
            className="bg-black hover:bg-gray-800 text-white"
            data-testid="share-download-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      </header>

      {/* Carousel */}
      <section className="flex-1 px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="aspect-video bg-gray-100 flex items-center justify-center">
              {currentThumbId ? (
                <img
                  src={`${API}/share/${token}/thumb/${currentThumbId}`}
                  alt={`Slide ${activeIdx + 1}`}
                  className="w-full h-full object-contain"
                  data-testid="share-active-slide"
                />
              ) : (
                <span className="text-sm text-gray-400">No preview available</span>
              )}
            </div>
            {total > 1 && (
              <>
                <button
                  onClick={() => setActiveIdx(i => Math.max(0, i - 1))}
                  disabled={activeIdx === 0}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/70 text-white flex items-center justify-center disabled:opacity-30 hover:bg-black/85"
                  data-testid="share-prev-btn"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setActiveIdx(i => Math.min(total - 1, i + 1))}
                  disabled={activeIdx === total - 1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/70 text-white flex items-center justify-center disabled:opacity-30 hover:bg-black/85"
                  data-testid="share-next-btn"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/70 text-white text-xs font-medium">
                  {activeIdx + 1} / {total}
                </div>
              </>
            )}
          </div>

          {/* Thumb rail */}
          {total > 1 && (
            <div
              ref={carouselRef}
              className="mt-5 flex gap-2 overflow-x-auto pb-2 scrollbar-thin"
              data-testid="share-thumb-rail"
            >
              {meta.slide_thumbnail_ids.map((id, idx) => (
                <button
                  key={`${id}-${idx}`}
                  data-rail-idx={idx}
                  onClick={() => setActiveIdx(idx)}
                  className={`flex-shrink-0 border-2 rounded-md overflow-hidden transition-all ${
                    idx === activeIdx ? "border-teal-500 shadow-sm" : "border-gray-200 hover:border-gray-300 opacity-80"
                  }`}
                  style={{ width: 120, height: 68 }}
                >
                  <img
                    src={`${API}/share/${token}/thumb/${id}`}
                    alt={`Slide ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Soft CTA */}
      <SoftCTA />
    </div>
  );
}


function SoftCTA() {
  return (
    <section className="border-t border-gray-200 bg-white">
      <div className="px-6 py-8 max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-teal-500" />
            Build your own decks like this
          </p>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
            Wokelo turns a topic, a few files, or a brief into investor-grade slides — in minutes. Free credits to start.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link to="/signup" data-testid="share-soft-cta-signup">
            <Button className="bg-black hover:bg-gray-800 text-white">
              Try Wokelo free
            </Button>
          </Link>
          <Link to="/login" className="text-xs text-gray-500 hover:text-gray-700">
            Sign in
          </Link>
        </div>
      </div>
    </section>
  );
}
